<template>
  <section v-show="!loadingService" class="eptp_content">
    <div class="container">
      <ul class="breadcrumbs">
        <li>
          <a :href="homeHref">{{ $t('pages.home.title') }}</a>
        </li>
        <li>
          <a :href="$url($t('pages.services.url'))">{{ $t('pages.services.title') }}</a>
        </li>
        <li>
          <a :href="$url(`${$t('pages.service.url')}?id=${service.id}`)">{{ service.name }}</a>
        </li>
      </ul>

      <BaseGlobalAlert />

      <template v-if="service.id">
        <a
          v-if="service.group"
          :href="$url(`${$t('pages.services.url')}?group=${service.group.code}`)"
          >{{ $t('pages.service.labels.serviceGroup') }}: {{ service.group.name }}</a
        >
        <div class="clearfix">
          <h1 class="pull-left">{{ service.name }}</h1>
          <ul class="buttons_row clearfix pull-right">
            <li v-if="canOrder" class="buttons_row__items buttons_row__items--full">
              <a :href="service.url" class="btn btn-feat btn-mobile-full"
                ><i class="icon-checkbox"></i> {{ $t('actions.order') }}</a
              >
            </li>
            <li class="buttons_row__items buttons_row__items--full">
              <a :href="$url($t('pages.services.url'))" class="btn btn-regular btn-mobile-full"
                ><i class="icon-close"></i> {{ $t('actions.cancel') }}</a
              >
            </li>
          </ul>
        </div>

        <table class="table table--first-medium">
          <tr v-if="service.surveyPublic || service.interactivityLevel || service.descriptionFull">
            <td data-label="">
              <a v-if="service.surveyPublic" :href="service.surveyPublic.url">{{
                service.surveyPublic.name
              }}</a>
              <h4 v-if="service.interactivityLevel">
                {{ $t('pages.service.labels.interactivityLevel') }} {{ service.interactivityLevel }}
              </h4>
            </td>
            <td>
              <p
                v-if="service.descriptionFull"
                v-html="service.descriptionFull"
                :class="{ 'margin-top': !service.surveyPublic }"
                style="white-space: pre-line;"
              ></p>
            </td>
          </tr>
          <tr v-if="service.steps">
            <td data-label="">
              <h4>{{ $t('pages.service.labels.steps') }}</h4>
            </td>
            <td>
              <p v-html="service.steps" class="margin-top" style="white-space: pre-line;"></p>
            </td>
          </tr>
          <tr v-if="service.paymentInfo">
            <td data-label="">
              <h4>{{ $t('pages.service.labels.paymentInfo') }}</h4>
            </td>
            <td>
              <p v-html="service.paymentInfo" class="margin-top" style="white-space: pre-line;"></p>
            </td>
          </tr>
          <tr v-if="service.providerInfo">
            <td data-label="">
              <h4>{{ $t('pages.service.labels.provider') }}</h4>
            </td>
            <td>
              <p v-if="service.providerInfo" class="margin-top">
                {{ service.providerInfo.name }}.
                <span
                  v-html="service.providerInfo.description"
                  style="white-space: pre-line;"
                ></span>
              </p>
            </td>
          </tr>
          <tr v-if="service.relatedLaw && service.relatedLaw.length">
            <td data-label="">
              <h4>{{ $t('pages.service.labels.relatedLaw') }}</h4>
            </td>
            <td>
              <div class="margin-top">
                <ul>
                  <li v-for="law in service.relatedLaw"
                      :key="law.code">
                    <a
                      :href="law.url"
                      target="_blank"
                      class="mr-1"
                      >{{ law.name }}</a
                    >
                  </li>
                </ul>
              </div>
            </td>
          </tr>
          <tr v-if="service.relatedFaq && service.relatedFaq.length">
            <td data-label="">
              <h4>{{ $t('pages.service.labels.relatedFaq') }}</h4>
            </td>
            <td>
              <div class="margin-top">
                <ul>
                  <li v-for="faq in service.relatedFaq"
                      :key="faq.code">
                    <a
                      :href="faq.url"
                      target="_blank"
                      class="mr-1"
                      >{{ faq.name }}</a
                    >
                  </li>
                </ul>
              </div>
            </td>
          </tr>
          <tr v-if="service.providerSpecialist && service.providerSpecialist.length">
            <td data-label="">
              <h4>{{ $t('pages.service.labels.providerSpecialist') }}</h4>
            </td>
            <td>
              <div class="row flex flex-wrap pb-4">
                <div
                  v-for="specialist in service.providerSpecialist"
                  :key="specialist.id"
                  class="col-xs-12 col-md-4 flex"
                >
                  <div class="full-width">
                    <div class="d-flex align-center pb-1 margin-top">
                      <i class="icon-user h-color--muted"></i>
                      <strong
                        >{{ specialist.firstname }} {{ specialist.lastname }}
                        <template v-if="specialist.position"
                          >({{ specialist.position }})</template
                        ></strong
                      >
                    </div>
                    <div v-if="specialist.description" class="pt-1 pb-2">
                      {{ specialist.description }}
                    </div>
                    <table class="full-width">
                      <tr v-if="specialist.phoneLocal" class="contact-row">
                        <td class="h-color--grey">
                          {{ $t('components.institutionPicker.table.headers.phone') }}
                        </td>

                        <td>
                          <a :href="`tel:${specialist.phoneLocal}`">{{ specialist.phoneLocal }}</a>
                        </td>
                      </tr>
                      <tr v-if="specialist.phoneMobile" class="contact-row">
                        <td class="h-color--grey">{{ $t('common.phoneMobile') }}</td>

                        <td>
                          <a :href="`tel:${specialist.phoneMobile}`">{{ specialist.phoneMobile }}</a>
                        </td>
                      </tr>
                      <tr v-if="specialist.email" class="contact-row">
                        <td class="h-color--grey">
                          {{ $t('components.institutionPicker.table.headers.email') }}
                        </td>

                        <td class="pl-1 py-0 pl-0">
                          <a :href="`mailto:${specialist.email}`">{{ specialist.email }}</a>
                        </td>
                      </tr>
                      <tr v-if="specialist.fax" class="contact-row">
                        <td class="h-color--grey">{{ $t('common.fax') }}</td>

                        <td>
                          <a :href="`fax:${specialist.fax}`">{{ specialist.fax }}</a>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </template>
    </div>

    <ImportantMessagesModal
      :loading="loadingImportantMessage"
      :message="displayedImportantMessage"
      @read="markImportantMessageAsRead"
    />
  </section>
</template>

<script>
import { isBefore } from 'date-fns';
import pageMixin from '../../mixins/page.mixin';
import serviceDirectoryService from '../../services/serviceDirectory.service';
import { getQueryParam } from '../../util/url';
import BaseGlobalAlert from '../../components/base/BaseGlobalAlert';

export default {
  name: 'Service',

  components: { BaseGlobalAlert },

  mixins: [pageMixin],

  data() {
    return {
      loadingService: true,
      service: {},
    };
  },

  computed: {
    canOrder() {
      if (!this.service.validTo) {
        return true;
      }
      return isBefore(new Date(), new Date(this.service.validTo));
    },
  },

  created() {
    const serviceId = getQueryParam('id');
    if (!serviceId) {
      window.location.href = this.$url(this.$t('pages.services.url'));
      return;
    }
    serviceDirectoryService
      .getById(serviceId)
      .then((res) => {
        this.service = res.data;
        document.title = `EPTP - ${this.service.name}`;
      })
      .finally(() => {
        this.loadingService = false;
      });
  },

  mounted() {
    this.setupPage();
  },
};
</script>

<style>
@import '../../../assets/css/style.min.css';
@import '../../assets/index.css';

.contact-row td:first-child {
  width: auto !important;
  max-width: unset !important;
  padding-right: 0.5rem;
}

.contact-row td {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
  padding-left: 0 !important;
}

.margin-top {
  margin-top: 10px !important;
}
</style>
